import { buildApiUrl } from '../config/api';

// Types
export interface MetadataSchemaField {
  id: number;
  key: string;
  detected_type: 'text' | 'enum' | 'number';
  user_override_type: 'text' | 'enum' | 'number' | null;
  effective_type: 'text' | 'enum' | 'number';
  sample_values: string[];
  last_validated_at: string | null;
}

export interface MetadataSchemaGroup {
  event_name: string;
  fields: MetadataSchemaField[];
}

export interface SegmentValue {
  value: string;
  count: number;
  percentage: number;
}

export interface Segment {
  id: number;
  event_name: string;
  key: string;
  values: SegmentValue[];
}

export type SegmentFilters = Record<string, string>;

// Helper function for authenticated requests
async function fetchWithAuth<T>(url: string, token: string): Promise<T> {
  const response = await fetch(url, {
    headers: {
      Authorization: `Bearer ${token}`,
      'Content-Type': 'application/json',
    },
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(errorData.error || `Request failed with status ${response.status}`);
  }

  return response.json();
}

async function patchWithAuth<T>(url: string, token: string, body: object): Promise<T> {
  const response = await fetch(url, {
    method: 'PATCH',
    headers: {
      Authorization: `Bearer ${token}`,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(body),
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(errorData.error || `Request failed with status ${response.status}`);
  }

  return response.json();
}

// Fetch all metadata schemas for a project
export async function fetchMetadataSchemas(
  projectId: number,
  token: string
): Promise<MetadataSchemaGroup[]> {
  const url = buildApiUrl(`api/v1/projects/${projectId}/metadata_schemas`);
  return fetchWithAuth<MetadataSchemaGroup[]>(url, token);
}

// Fetch segmentable fields with their values
export async function fetchSegments(
  projectId: number,
  token: string
): Promise<Segment[]> {
  const url = buildApiUrl(`api/v1/projects/${projectId}/metadata_schemas/segments`);
  return fetchWithAuth<Segment[]>(url, token);
}

// Update a metadata schema's type override
export async function updateMetadataSchemaType(
  projectId: number,
  schemaId: number,
  type: 'text' | 'enum' | 'number' | null,
  token: string
): Promise<MetadataSchemaField> {
  const url = buildApiUrl(`api/v1/projects/${projectId}/metadata_schemas/${schemaId}`);
  return patchWithAuth<MetadataSchemaField>(url, token, { type });
}

// Build query string for segment filters
export function buildSegmentQueryParams(filters: SegmentFilters): string {
  const params = new URLSearchParams();
  Object.entries(filters).forEach(([key, value]) => {
    if (value) {
      params.append(`segment[${key}]`, value);
    }
  });
  return params.toString();
}
